<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Responsive Component
      p.text--secondary A responsive container component based on Vuetify's VResponsive for maintaining aspect ratios and responsive layouts.

    // Responsive Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in responsiveVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped responsive containers (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              .d-flex.flex-wrap.gap-4.align-start
                div(v-for="resp in variation.containers" :key="resp.label" class="flex-shrink-0")
                  div.mb-2
                    small.font-weight-medium {{ resp.label }}
                  yz-responsive(v-bind="resp.props")
                    div.d-flex.align-center.justify-center.fill-height(style="background: linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%); background-size: 20px 20px; background-position: 0 0, 0 10px, 10px -10px, -10px 0px;")
                      span.text-h6.text--secondary {{ resp.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="resp in variation.containers" :key="resp.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === resp.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === resp.code ? 'success' : ''" @click="copyCode(resp.code)")
                    span.font-weight-medium.ml-2 {{ resp.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ resp.code }}

          // Individual responsive containers
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-responsive(v-bind="variation.props")
                div.d-flex.align-center.justify-center.fill-height(style="background: linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%); background-size: 20px 20px; background-position: 0 0, 0 10px, 10px -10px, -10px 0px;")
                  span.text-h6.text--secondary {{ variation.hint }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in responsiveProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      responsiveVariations: [
        {
          name: 'Common Aspect Ratios',
          id: 'common-aspect-ratios',
          description: 'Standard aspect ratios for different media types',
          isGroup: true,
          containers: [
            {
              label: '16:9 Widescreen',
              props: {
                aspectRatio: 16/9,
                width: '200'
              },
              code: 'yz-responsive(:aspect-ratio="16/9" width="200")\n  // Content maintains 16:9 ratio',
              hint: '16:9'
            },
            {
              label: '4:3 Standard',
              props: {
                aspectRatio: 4/3,
                width: '200'
              },
              code: 'yz-responsive(:aspect-ratio="4/3" width="200")\n  // Content maintains 4:3 ratio',
              hint: '4:3'
            },
            {
              label: '1:1 Square',
              props: {
                aspectRatio: 1,
                width: '150'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="150")\n  // Square container',
              hint: '1:1'
            },
            {
              label: '3:2 Photo',
              props: {
                aspectRatio: 3/2,
                width: '180'
              },
              code: 'yz-responsive(:aspect-ratio="3/2" width="180")\n  // Photo aspect ratio',
              hint: '3:2'
            }
          ]
        },
        {
          name: 'Social Media Ratios',
          id: 'social-media-ratios',
          description: 'Aspect ratios commonly used in social media platforms',
          isGroup: true,
          containers: [
            {
              label: 'Instagram Post',
              props: {
                aspectRatio: 1,
                width: '160'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="160")\n  // Instagram square post',
              hint: 'IG Post'
            },
            {
              label: 'Instagram Story',
              props: {
                aspectRatio: 9/16,
                width: '120'
              },
              code: 'yz-responsive(:aspect-ratio="9/16" width="120")\n  // Instagram story format',
              hint: 'IG Story'
            },
            {
              label: 'YouTube Thumbnail',
              props: {
                aspectRatio: 16/9,
                width: '200'
              },
              code: 'yz-responsive(:aspect-ratio="16/9" width="200")\n  // YouTube thumbnail',
              hint: 'YT Thumb'
            },
            {
              label: 'Twitter Card',
              props: {
                aspectRatio: 2/1,
                width: '200'
              },
              code: 'yz-responsive(:aspect-ratio="2/1" width="200")\n  // Twitter card format',
              hint: 'Twitter'
            }
          ]
        },
        {
          name: 'Video Formats',
          id: 'video-formats',
          description: 'Common video and cinema aspect ratios',
          isGroup: true,
          containers: [
            {
              label: 'HD Video',
              props: {
                aspectRatio: 16/9,
                width: '240'
              },
              code: 'yz-responsive(:aspect-ratio="16/9" width="240")\n  // Standard HD video',
              hint: '1920×1080'
            },
            {
              label: 'Ultrawide',
              props: {
                aspectRatio: 21/9,
                width: '280'
              },
              code: 'yz-responsive(:aspect-ratio="21/9" width="280")\n  // Ultrawide cinema',
              hint: '21:9'
            },
            {
              label: 'Vertical Video',
              props: {
                aspectRatio: 9/16,
                width: '135'
              },
              code: 'yz-responsive(:aspect-ratio="9/16" width="135")\n  // Mobile vertical video',
              hint: '9:16'
            }
          ]
        },
        {
          name: 'Custom Dimensions',
          id: 'custom-dimensions',
          description: 'Responsive containers with custom width and height constraints',
          isGroup: true,
          containers: [
            {
              label: 'Max Width',
              props: {
                aspectRatio: 16/9,
                maxWidth: '300',
                width: '100%'
              },
              code: 'yz-responsive(:aspect-ratio="16/9" max-width="300" width="100%")\n  // Responsive with max width',
              hint: 'Max 300px'
            },
            {
              label: 'Min Height',
              props: {
                aspectRatio: 4/3,
                minHeight: '120',
                width: '180'
              },
              code: 'yz-responsive(:aspect-ratio="4/3" min-height="120" width="180")\n  // Minimum height constraint',
              hint: 'Min 120px'
            },
            {
              label: 'Fixed Height',
              props: {
                height: '120',
                width: '200'
              },
              code: 'yz-responsive(height="120" width="200")\n  // Fixed dimensions',
              hint: '200×120'
            }
          ]
        },
        {
          name: 'Card Layouts',
          id: 'card-layouts',
          description: 'Responsive containers for card-based layouts',
          isGroup: true,
          containers: [
            {
              label: 'Card Header',
              props: {
                aspectRatio: 3/1,
                width: '240'
              },
              code: 'yz-responsive(:aspect-ratio="3/1" width="240")\n  // Card header area',
              hint: 'Header'
            },
            {
              label: 'Card Image',
              props: {
                aspectRatio: 16/10,
                width: '200'
              },
              code: 'yz-responsive(:aspect-ratio="16/10" width="200")\n  // Card image area',
              hint: 'Image'
            },
            {
              label: 'Card Square',
              props: {
                aspectRatio: 1,
                width: '160'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="160")\n  // Square card',
              hint: 'Square'
            }
          ]
        },
        {
          name: 'Responsive Grids',
          id: 'responsive-grids',
          description: 'Using responsive containers in grid layouts',
          isGroup: true,
          containers: [
            {
              label: 'Grid Item 1',
              props: {
                aspectRatio: 1,
                width: '100'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="100")\n  // Grid cell',
              hint: 'Cell 1'
            },
            {
              label: 'Grid Item 2',
              props: {
                aspectRatio: 1,
                width: '100'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="100")\n  // Grid cell',
              hint: 'Cell 2'
            },
            {
              label: 'Grid Item 3',
              props: {
                aspectRatio: 1,
                width: '100'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="100")\n  // Grid cell',
              hint: 'Cell 3'
            },
            {
              label: 'Grid Item 4',
              props: {
                aspectRatio: 1,
                width: '100'
              },
              code: 'yz-responsive(:aspect-ratio="1" width="100")\n  // Grid cell',
              hint: 'Cell 4'
            }
          ]
        }
      ],
      responsiveProps: [
        {
          name: 'aspectRatio',
          type: 'Number',
          default: 'undefined',
          description: 'Aspect ratio to maintain (width/height). Example: 16/9 for widescreen'
        },
        {
          name: 'contentClass',
          type: 'String',
          default: 'undefined',
          description: 'CSS class to apply to the content wrapper'
        },
        {
          name: 'height',
          type: 'Number | String',
          default: 'undefined',
          description: 'Height of the container in pixels'
        },
        {
          name: 'width',
          type: 'Number | String',
          default: 'undefined',
          description: 'Width of the container in pixels or percentage'
        },
        {
          name: 'maxHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum height constraint'
        },
        {
          name: 'maxWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Maximum width constraint'
        },
        {
          name: 'minHeight',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum height constraint'
        },
        {
          name: 'minWidth',
          type: 'Number | String',
          default: 'undefined',
          description: 'Minimum width constraint'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-4 > *:not(:last-child) {
  margin-right: 16px;
}
</style>